import { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import Immutable from 'immutable';
import { getProductDiscountTiers } from '../../helpers';

export interface DiscountTier {
  min_qty: number;
  max_qty: number | null;
  percentage: number;
}

interface CartProductHook {
  cartProduct: any;
  cartProductId: string | undefined;
  cartProductQuantity: number;
  localQuantity: number | string;
  setLocalQuantity: (value: number | string) => void;
  cartProductPrice: number;
  cartProductImage: any;
  maximumQuantity: number | undefined;
  discountTiers: DiscountTier[];
  body: string | undefined;
}

/**
 * Custom hook to access the first cart product and manage quantity state
 * Retrieves product data from Redux and maintains local quantity state for form input
 * @returns Object containing cart product data and quantity state management
 */
const useCartProduct = (): CartProductHook => {
  // Get first cart product from Redux commerce state
  const cartProduct = useSelector((state: any) => state.getIn(['commerce', 'cart', 'products'], Immutable.List()).get(0));

  // Extract product ID and quantity from cart product
  const cartProductId = cartProduct?.get('id');
  const cartProductQuantity = cartProduct?.getIn(['attributes', 'quantity'], 1);

  // Local state for input - can be string while user is typing, converted to number when needed
  const [localQuantity, setLocalQuantity] = useState<number | string>(
    Math.floor(cartProductQuantity || 1),
  );

  // Get product image from relationships
  const cartProductImage = cartProduct?.getIn([
    'relationships',
    'purchased_entity',
    'relationships',
    'product_id',
    'relationships',
    'field_image',
  ]);

  // Extract price using getIn instead of toJS() for efficiency
  const cartProductPrice = cartProduct?.getIn(
    ['attributes', 'unit_price', 'number'],
    0,
  ) as number;

  // Extract maximum order quantity from the product variation
  const maxQty = cartProduct?.getIn([
    'relationships',
    'purchased_entity',
    'attributes',
    'maximum_order_quantity',
  ]);
  const parsed = maxQty ? Number(maxQty) : NaN;
  const maximumQuantity =
    !Number.isNaN(parsed) && parsed > 0 ? parsed : undefined;

  // Product description (body), shown under the title when filled in.
  const body = cartProduct?.getIn([
    'relationships',
    'purchased_entity',
    'relationships',
    'product_id',
    'attributes',
    'body',
    'value',
  ]) as string | undefined;

  const discountTiers = useMemo<DiscountTier[]>(
    () => getProductDiscountTiers(cartProduct),
    [cartProduct],
  );

  return {
    cartProduct,
    cartProductId,
    cartProductQuantity,
    localQuantity,
    cartProductPrice,
    cartProductImage,
    setLocalQuantity,
    maximumQuantity,
    discountTiers,
    body,
  };
};

export default useCartProduct;
